import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { goEntitiesApi } from "@/services/go-entities.service";
import { notifyModuleError, notifyModuleSuccess } from "@/lib/module-toast";
import { useToast } from "@/hooks/use-toast";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ginfObjId: number;
  onApplied: () => void;
};

export function PrepareClassifierDialog({ open, onOpenChange, ginfObjId, onApplied }: Props) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const profilesQuery = useQuery({
    queryKey: ["go-entities", "country-profiles"],
    queryFn: () => goEntitiesApi.listCountryProfiles(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setSelected([]);
      setPreviewCount(null);
    }
  }, [open]);

  const toggle = (code: string) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
    setPreviewCount(null);
  };

  const runDry = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const res = await goEntitiesApi.prepareClassifier(ginfObjId, {
        profiles: selected,
        dry_run: true,
      });
      setPreviewCount(res.would_mark_na.length);
      notifyModuleSuccess(
        toast,
        res.message || `Будет N/A: ${res.would_mark_na.length}`,
      );
    } catch (e) {
      notifyModuleError(toast, "dry-run подготовки классификатора", e);
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const res = await goEntitiesApi.prepareClassifier(ginfObjId, {
        profiles: selected,
        dry_run: false,
      });
      notifyModuleSuccess(toast, res.message || `N/A: ${res.marked_na.length}`);
      onApplied();
      onOpenChange(false);
    } catch (e) {
      notifyModuleError(toast, "подготовку классификатора", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[220]">
        <DialogHeader>
          <DialogTitle>Подготовить классификатор</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Выберите профили страны. Dry-run покажет, сколько пустых полей станет N/A (заполненные не
          затираются).
        </p>
        <div className="max-h-[40vh] space-y-2 overflow-y-auto py-2">
          {profilesQuery.isLoading ? (
            <div className="text-xs text-muted-foreground">Загрузка профилей…</div>
          ) : (profilesQuery.data?.length ?? 0) === 0 ? (
            <div className="text-xs text-muted-foreground">Нет профилей (нужна миграция seed).</div>
          ) : (
            profilesQuery.data!.map((p) => (
              <label
                key={p.code}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-border px-3 py-2"
              >
                <Checkbox
                  checked={selected.includes(p.code)}
                  onCheckedChange={() => toggle(p.code)}
                />
                <span className="space-y-0.5">
                  <Label className="cursor-pointer font-medium">{p.label}</Label>
                  <span className="block text-[11px] text-muted-foreground">
                    {p.code}
                    {p.description ? ` — ${p.description}` : ""}
                  </span>
                  {p.auto_na_prefixes?.length ? (
                    <span className="block text-[10px] text-muted-foreground">
                      префиксы: {p.auto_na_prefixes.join(", ")}
                    </span>
                  ) : (
                    <span className="block text-[10px] text-muted-foreground">без авто-N/A</span>
                  )}
                </span>
              </label>
            ))
          )}
        </div>
        {previewCount != null ? (
          <p className="text-xs text-foreground">Превью: будет помечено N/A — {previewCount}</p>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={busy || selected.length === 0}
            onClick={() => void runDry()}
          >
            Dry-run
          </Button>
          <Button
            type="button"
            disabled={busy || selected.length === 0}
            onClick={() => void runApply()}
          >
            Применить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
